import { useMemo, useState } from "react";
import { useLogoStore } from "../../state/logoStore";
import { scoreLogo } from "../../engine/aestheticScore";
import { ColorRow, gradeClass } from "./EditorControls";

// ============================================================
// Aesthetic score panel — Birkhoff M = O/(1+C) + Wu 2025 stability mediator
// ============================================================
export function ScorePanel() {
  const s = useLogoStore();
  const result = useMemo(() => scoreLogo({ state: s }), [
    s.title, s.slogan, s.iconId, s.qrText, s.titleColor, s.sloganColor, s.iconColor,
    s.background, s.layout, s.titleSize, s.sloganSize, s.iconScale, s.customIcons,
  ]);
  const b = result.breakdown;
  const bars: Array<[string, number, string]> = [
    ["Vertical symmetry",   b.mirrorV,     "Mirror axis through the center — strongest readability cue."],
    ["Color harmony",       b.colorHarmony,"Distance to musical-style hue harmonics on the color wheel."],
    ["Equilibrium",         b.equilibrium, "Visual weight balance from the chosen layout."],
    ["φ alignment",         b.phiAlign,    "Title/tagline size ratio close to 1.618 (golden)."],
    ["Rotational",          b.rotational,  "Symmetry under rotation — bonus for radial marks."],
    ["Translational",       b.translational,"Repetition / structural rhythm."],
    ["Legibility",          b.legibility,  "Element count under 4 = optimal."],
  ];

  return (
    <>
      <div className={`score-hero grade-${gradeClass(result.grade)}`}>
        <div className="score-hero-grade">{result.grade}</div>
        <div className="score-hero-num">{result.scorePercent}<span>/100</span></div>
        <div className="score-hero-sub">
          M = {result.M_raw.toFixed(2)} · stability {(result.stability * 100).toFixed(0)} · memorability {(result.memorability * 100).toFixed(0)}
        </div>
      </div>

      <div className="score-section-head">Aesthetic axes</div>
      <div className="score-bars">
        {bars.map(([label, val, tip]) => (
          <div key={label} className="score-bar" title={tip}>
            <div className="score-bar-row">
              <span>{label}</span>
              <strong>{(val * 100).toFixed(0)}</strong>
            </div>
            <div className="score-bar-track">
              <div className={`score-bar-fill ${val < 0.4 ? "low" : val < 0.7 ? "mid" : "high"}`} style={{ width: `${Math.round(val * 100)}%` }} />
            </div>
          </div>
        ))}
      </div>

      <div className="score-section-head">Suggestions</div>
      <ul className="score-feedback">
        {result.feedback.map((f, i) => <li key={i}>{f}</li>)}
      </ul>

      <details className="score-method">
        <summary>How this is calculated</summary>
        <p>Birkhoff M = Order ÷ (1 + Complexity), then weighted by Wu 2025 stability mediator (symmetry → stability → quality) and a memorability factor (legibility + color restraint). Symmetry decomposed by Wagemans triad. Color harmony measures hue-circle distance to standard harmonics (0°/30°/60°/90°/120°/150°/180°).</p>
      </details>
    </>
  );
}

// ============================================================
// Custom icons panel — drop / pick a .svg, becomes selectable
// ============================================================
export function CustomIconsPanel() {
  const { customIcons, iconId, setIcon, removeCustomIcon, pushHistory, addCustomIcon } = useLogoStore();

  async function handleFile(f: File) {
    if (!f.name.toLowerCase().endsWith(".svg")) {
      alert("Choose an .svg file"); return;
    }
    try {
      const { importSvgAsIcon } = await import("../../engine/svgImporter");
      const icon = await importSvgAsIcon(f);
      pushHistory();
      addCustomIcon({
        id: icon.id, name: icon.name, paths: icon.paths,
        vb: icon.viewBox, addedAt: new Date().toISOString(),
      });
      setIcon(icon.id);
    } catch (e) {
      alert("Failed to import SVG: " + (e instanceof Error ? e.message : String(e)));
    }
  }

  return (
    <>
      <label className="dropzone" style={{ marginBottom: 12 }}>
        <input type="file" accept=".svg" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
        <div className="dropzone-inner">
          Drop or pick a .svg file
          <div className="dropzone-hint">Becomes a selectable icon, scaled into your logo</div>
        </div>
      </label>

      {customIcons.length === 0 ? (
        <div style={{ fontSize: 12, color: "#94a3b8", padding: "20px 0", textAlign: "center" }}>
          No custom icons yet — drop an SVG above.
        </div>
      ) : (
        <div className="icon-grid">
          {customIcons.map((i) => (
            <div key={i.id} className="custom-tile-wrap">
              <button
                className={`icon-tile ${iconId === i.id ? "active" : ""}`}
                onClick={() => { pushHistory(); setIcon(i.id); }}
                title={i.name}
              >
                <svg viewBox={`${i.vb.x} ${i.vb.y} ${i.vb.w} ${i.vb.h}`} width="36" height="36">
                  {i.paths.map((d, idx) => <path key={idx} d={d} fill="#0f172a" />)}
                </svg>
              </button>
              <button
                className="custom-tile-x"
                title="Delete custom icon"
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm(`Remove custom icon "${i.name}"?`)) {
                    if (iconId === i.id) setIcon(null);
                    removeCustomIcon(i.id);
                  }
                }}
              >×</button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ============================================================
// QR code panel — type a URL, becomes a scannable icon
// ============================================================
export function QrCodePanel() {
  const { qrText, setQrText, setIcon, pushHistory, iconId, iconColor, setStyle } = useLogoStore();
  const [draft, setDraft] = useState(qrText || "");
  const isQr = iconId === "__qr__";

  function apply() {
    const v = draft.trim();
    if (!v) { alert("Enter a URL or text first"); return; }
    pushHistory();
    setQrText(v);
    setIcon("__qr__");
  }

  function clearQr() {
    pushHistory();
    if (isQr) setIcon(null);
    setQrText(null);
    setDraft("");
  }

  return (
    <>
      <label className="field">
        <span>QR text or URL</span>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="https://yourbrand.com"
          onKeyDown={(e) => { if (e.key === "Enter") apply(); }}
        />
      </label>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn primary" style={{ flex: 1 }} onClick={apply}>{isQr ? "Update QR" : "Use as icon"}</button>
        {isQr && <button className="btn ghost" onClick={clearQr}>Remove</button>}
      </div>

      {isQr && (
        <>
          <div style={{ marginTop: 14 }}>
            <ColorRow
              label="QR color"
              color={iconColor}
              onChange={(c) => { pushHistory(); setStyle({ iconColor: c }); }}
            />
          </div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 8, lineHeight: 1.5 }}>
            Tip: QR codes need a solid surface to scan. Pair a dark QR color with a light <strong>Logo frame</strong> (Background tab) so scanners read it cleanly.
          </div>
        </>
      )}

      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 12, lineHeight: 1.5 }}>
        Generates a scannable QR code as a vector. Works in your logo, brand kit ZIP, and on business cards.
      </div>
    </>
  );
}
